import { getAddOns, isAdditionalInformationQuestionsExistInForm, isEquipmentBased, isScheduled } from "common";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Experience, LineItemType } from "types";
import shallow from "zustand/shallow";
import { CreatePurchaseAddOnField } from "./CreatePurchaseAddOnField";
import { CreatePurchaseArrivalPicker } from "./CreatePurchaseArrivalPicker";
import { CreatePurchaseAttendeesPicker } from "./CreatePurchaseAttendeesPicker";
import { CreatePurchaseDemographicPicker } from "./CreatePurchaseDemographicPicker";
import { CreatePurchaseEquipmentPicker } from "./CreatePurchaseEquipmentPicker";
import { CreatePurchasePrivacy } from "./CreatePurchasePrivacy";
import { CustomLineItemList } from "./CustomLineItemList";
import { GuestQuestionnaireList } from "./GuestQuestionnaire/GuestQuestionnaireList";
import { useInitializeEmv } from "./hooks/useInitializeEmv";
import { OrganizerQuestionnaireList } from "./OrganizerQuestionnaire/OrganizerQuestionnaireList";
import { ProductAvailabilityAlert } from "./ProductAvailabilityAlert";
import { getValueFromGratuitySelection, GratuitySelector } from "@/components/PurchaseCreate/GratuitySelector";
import { hasUniqueGuestIdentification } from "@/models/Experience";
import { getGratuityEnabledExperiences, hasGratuityEnabledForBackOffice } from "@/models/Seller";
import { GratuityLineItem, GratuitySelection, useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { callDebounced } from "@/utils/debounceUtils";
import { getSeller } from "@/utils/getSeller";

interface CreatePurchaseExperienceFieldsProps {
    experience: Experience;
    showQuestionnaireList?: boolean;
}

const handleItemGratuityChange = (
    gratuitySelection: GratuitySelection,
    activeItemIndex: number,
    itemUnitSubtotal: number,
) => {
    const { setActiveItemGratuitySelection, setPurchase } = useCreatePurchaseStore.getState().actions;
    setActiveItemGratuitySelection(gratuitySelection.amount, gratuitySelection.algorithm, gratuitySelection.custom);
    setPurchase((draft) => {
        const item = draft.items[activeItemIndex];
        const currentGratuityLineItem = item.lineItems.find(
            (lineItem) => lineItem.type === LineItemType.GRATUITY,
        ) as GratuityLineItem;
        const currentGratuityValue = Number(currentGratuityLineItem?.amount ?? 0);

        const selectedGratuity = getValueFromGratuitySelection(gratuitySelection, itemUnitSubtotal);
        const selectedGratuityValue = Math.round(selectedGratuity * 100) / 100;

        // early exit if nothing changed to prevent infinite re-renders
        if (selectedGratuityValue === currentGratuityValue) {
            return;
        }

        // remove old gratuity
        item.lineItems = item.lineItems.filter((li) => li.type !== LineItemType.GRATUITY);

        if (selectedGratuityValue > 0) {
            item.lineItems.push({
                type: LineItemType.GRATUITY,
                amount: selectedGratuityValue,
                name: "Gratuity",
            });
        }
    });
};

export const CreatePurchaseExperienceFields = ({
    experience,
    showQuestionnaireList = false,
}: CreatePurchaseExperienceFieldsProps) => {
    useInitializeEmv();

    const history = useHistory();
    const shouldShowArrivalPicker = isScheduled(experience);
    const hasAddOns = getAddOns(experience).length > 0;
    const isAttendeeUnique = hasUniqueGuestIdentification(experience);

    const [activeItem, activeItemIndex, itemUnitSubtotal] = useCreatePurchaseStore((state) => {
        return [
            state.getActiveItem(),
            state.activeItemIndex,
            state.getItemUnitSubtotal(state.getActiveItem()?.id ?? "") ?? 0,
        ];
    }, shallow);
    const seller = getSeller();
    const form = useCreatePurchaseStore((state) => state.getForm());
    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);
    const isPartnerExperience = experience.seller.id !== seller.id;
    const gratuityEnabledExperiences = getGratuityEnabledExperiences();
    const isGratuityEnabledForExperience =
        gratuityEnabledExperiences.all || gratuityEnabledExperiences.experiences.includes(experience.id);
    const shouldShowGratuity =
        hasGratuityEnabledForBackOffice() && !isPartnerExperience && isGratuityEnabledForExperience;

    const hasOrganizerQuestions = isAdditionalInformationQuestionsExistInForm(form);

    useEffect(() => {
        return () => {
            const isMist = useCreatePurchaseStore.getState().isMist();
            if (history.action === "POP" && !isMist) {
                /** User has clicked on a product in create purchase flow and clicked browser back button, so we need to reset the CreatePurchase state to initial */
                resetState();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <div className="w-full space-y-8">
                <CreatePurchasePrivacy />
                <ProductAvailabilityAlert experience={experience} />
                {isEquipmentBased(experience) ? (
                    <>
                        <CreatePurchaseEquipmentPicker />
                        <CreatePurchaseAttendeesPicker />
                    </>
                ) : (
                    <CreatePurchaseDemographicPicker />
                )}
                {shouldShowArrivalPicker && (
                    <div className="w-full shrink-0 2xl:w-3/4">
                        <CreatePurchaseArrivalPicker />
                    </div>
                )}

                {hasAddOns && (
                    <>
                        <hr className="mb-2 text-gray-light" />
                        <div className="!mt-0">
                            <CreatePurchaseAddOnField />
                        </div>
                    </>
                )}

                <hr className="text-gray-light" />
                {shouldShowGratuity && (
                    <>
                        <div className="w-full">
                            <GratuitySelector
                                item={activeItem}
                                itemUnitSubtotal={itemUnitSubtotal}
                                onGratuityChange={(gratuitySelection: GratuitySelection) =>
                                    callDebounced(
                                        500,
                                        handleItemGratuityChange,
                                        gratuitySelection,
                                        activeItemIndex,
                                        itemUnitSubtotal,
                                    )
                                }
                            />
                        </div>
                        <hr className="text-gray-light" />
                    </>
                )}
                {showQuestionnaireList && hasOrganizerQuestions && isAttendeeUnique && (
                    <OrganizerQuestionnaireList isAttendeeUnique label="Additional Information" />
                )}

                {showQuestionnaireList &&
                    (isAttendeeUnique ? <GuestQuestionnaireList /> : <OrganizerQuestionnaireList />)}

                <CustomLineItemList />
            </div>
        </div>
    );
};
